import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import LegalFooter from "../components/LegalFooter";

export default function Terms() {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground" data-testid="terms-page">
      <header className="px-6 py-5 border-b border-border/60 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          data-testid="terms-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
        <span className="brand-wordmark text-sm">FINALLOOK</span>
      </header>

      <main className="flex-1 px-6 py-10">
        <article className="max-w-2xl mx-auto space-y-6 text-sm leading-relaxed">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mb-2" data-testid="terms-title">
              Terms of Service
            </h1>
            <p className="text-xs text-muted-foreground" data-testid="terms-effective-date">
              Effective date: February 20, 2026
            </p>
          </div>

          <section>
            <p>
              These Terms of Service ("Terms") govern your access to and use of FINALLOOK, an
              AI-powered communication review service ("Service") provided by FINALLOOK
              ("FINALLOOK," "we," "us," or "our"). By creating an account or using the Service you
              agree to these Terms. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">1. The Service</h2>
            <p>
              FINALLOOK lets you draft a text message, submit it for an AI review of tone and
              clarity, and — only after your explicit in-app approval — deliver the message to a
              recipient via SMS. The Service never sends a message without your action.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">2. Eligibility</h2>
            <p>
              You must be at least 13 years old to use the Service. By using the Service you
              represent that all information you provide is accurate and that you have the right to
              contact any recipient whose phone number you enter.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">3. Your Account</h2>
            <p>
              You are responsible for maintaining the confidentiality of your credentials and for
              all activity under your account. Notify us promptly of any unauthorized use.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">4. Acceptable Use</h2>
            <p className="mb-2">You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Send unsolicited commercial or marketing messages (spam).</li>
              <li>
                Send messages that are unlawful, harassing, hateful, defamatory, threatening, or
                that violate another person's privacy.
              </li>
              <li>
                Contact any recipient who has requested to stop receiving messages from you (see
                Section 5).
              </li>
              <li>
                Attempt to bypass rate limits, reverse-engineer the Service, or interfere with its
                operation.
              </li>
              <li>Use the Service to impersonate another person or entity.</li>
            </ul>
          </section>

          <section className="rounded-lg border border-border/70 p-4 bg-secondary/30">
            <h2 className="font-heading font-semibold text-lg mb-2">5. SMS Terms</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Explicit opt-in per message:</strong> FINALLOOK only transmits an SMS after
                you tap the in-app approval button for that specific message. There are no
                recurring campaigns, autoresponders, or promotional broadcasts.
              </li>
              <li>
                <strong>Message frequency:</strong> variable — a message is sent only when you
                approve one.
              </li>
              <li>
                <strong>Message &amp; data rates may apply</strong> from the carrier of the
                recipient and, where applicable, the sender.
              </li>
              <li>
                <strong>Opt-out:</strong> a recipient may reply <strong>STOP</strong> to any
                message to stop receiving further messages from that number. Reply{" "}
                <strong>HELP</strong> for assistance.
              </li>
              <li>
                <strong>Carrier disclaimer:</strong> carriers (including but not limited to AT&amp;T,
                T-Mobile, Verizon, and their affiliates) are not liable for delayed or undelivered
                messages.
              </li>
              <li>
                <strong>Consent to contact:</strong> you represent that each recipient whose number
                you enter has consented to receive text messages from you, and that your use of the
                Service complies with the Telephone Consumer Protection Act (TCPA), CAN-SPAM, and
                all other applicable laws.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">6. AI Output Disclaimer</h2>
            <p>
              The AI review is a suggestion, not professional advice. AI models can make mistakes
              and may misinterpret context. You are always the final decision-maker on whether and
              how to send any message.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">7. Intellectual Property</h2>
            <p>
              FINALLOOK and its content (excluding your submitted drafts and messages) are owned by
              us and protected by intellectual-property law. You retain ownership of the content
              you submit and grant us a limited license to process it as needed to provide the
              Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">8. Disclaimers</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND,
              EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT. WE DO NOT GUARANTEE THAT SMS MESSAGES WILL BE
              DELIVERED OR DELIVERED WITHIN A SPECIFIC TIME.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, FINALLOOK WILL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF
              PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, ARISING FROM YOUR USE
              OF THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">10. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time if we believe you
              have violated these Terms. You may stop using the Service at any time by deleting
              your account.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">11. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the United States and, to the extent
              applicable, the state in which FINALLOOK maintains its principal place of business,
              without regard to conflict-of-law principles.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">12. Changes</h2>
            <p>
              We may update these Terms from time to time. Material changes will be communicated in
              the app or by email. Continued use of the Service after changes take effect
              constitutes your acceptance.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">13. Contact Us</h2>
            <p>
              Questions about these Terms? Email us at{" "}
              <a
                href="mailto:gregory@finallookapp.com"
                className="text-primary hover:underline"
                data-testid="terms-contact-email"
              >
                gregory@finallookapp.com
              </a>
              .
            </p>
          </section>
        </article>
      </main>

      <LegalFooter />
    </div>
  );
}
